
// Client-side counterpart to tools/parse_ler_gml.py: parses LER
// (Ledningsejerregisteret) data directly in the browser, so a
// graveforespoergsel GML export - or a live fetch from a municipality's WFS
// (see LERWFS.js) - can be loaded straight into Potree without any offline
// preprocessing step. Ground elevation (needed whenever a feature's Z isn't
// specified) is sampled from whichever point cloud is currently loaded -
// see buildGroundSampler() below - so this works for whatever site/point
// cloud the user currently has open, not just one fixed dataset.
//
// Two on-the-wire schemas are understood, auto-detected per feature:
//
//  - The official LER export schema: one GML element type per supply/kind
//    (ler:Elledning, ler:Vandkomponent, ler:Ledningstrace, ...), wrapped in
//    <gml:featureMember>, with camelCase ler:-namespaced attributes.
//  - A municipality's own WFS (e.g. a GeoServer-backed "LERGIS" system):
//    everything flattened into one generic layer per kind
//    (*:valid_ledninger / *:valid_komponenter, or older *_lergis_* /
//    old_* variants), wrapped in WFS 2.0's <wfs:member>, with snake_case
//    unprefixed attributes and a `table_name` column identifying the
//    original per-supply type (e.g. "afloebsledning"). Real, measured Z is
//    far more common in this schema (a `topkote`/`bundkote` pair, and the
//    geometry's own Z, are often already populated) - only fall back to
//    ground/depth when the geometry Z is still the "not specified" sentinel.

const GML_NS = "http://www.opengis.net/gml/3.2";
const LER_NS = "http://data.gov.dk/schemas/LER/2/gml";
const WFS_NS = "http://www.opengis.net/wfs/2.0";

const LINE_TYPES = {
	Afloebsledning: "afloeb",
	Elledning: "el",
	Foeringsroer: "foeringsroer",
	TermiskLedning: "termisk",
	Vandledning: "vand",
};

const POINT_TYPES = {
	Afloebskomponent: "afloeb",
	Elkomponent: "el",
	Telekommunikationskomponent: "telekom",
	TermiskKomponent: "termisk",
	Vandkomponent: "vand",
};

const TRACE_TYPE = "Ledningstrace";

const TRACE_SUPPLY = {
	"el": "el",
	"telekommunikation": "telekom",
	"afloeb": "afloeb",
	"spildevand": "afloeb",
	"vand": "vand",
	"fjernvarme": "termisk",
	"varme": "termisk",
};

// table_name (flattened WFS schema) -> supply key, matched by prefix since
// table names follow "<supply><ledning|komponent>", e.g. "afloebsledning",
// "telekommunikationskomponent".
const SUPPLY_PREFIXES = [
	["afloeb", "afloeb"],
	["spildevand", "afloeb"],
	["telekommunikation", "telekom"],
	["foering", "foeringsroer"],
	["termisk", "termisk"],
	["fjernvarme", "termisk"],
	["vand", "vand"],
	["el", "el"],
];

export const DEFAULT_DIAMETER_M = 0.1;

// Assumed burial depth (top-of-pipe below ground) per supply type, used
// only when a feature has neither a real measured Z nor its own
// vejledendeDybde. LE34's own standard "lægge dybde" per type.
const DEFAULT_DEPTHS_M = {
	el: 0.35,
	vand: 1.2,
	afloeb: 1.0,
	foeringsroer: 0.40,
	telekom: 0.40,
	termisk: 0.70,
};

function defaultDepthFor(supply, fallbackM){
	return DEFAULT_DEPTHS_M[supply] !== undefined ? DEFAULT_DEPTHS_M[supply] : fallbackM;
}

// LER's documented "not specified" sentinel for Z is -99; some exporters
// (and this file's Foeringsroer features) also emit a bare 0 for the same
// meaning.
// The official LER export's own documented "not specified" sentinel is
// -99; a bare 0 means the same for some exporters. Found live against a
// real municipal WFS (Aarhus/dannevang_kommune): some per-vertex Z values
// come through as -989 instead - an undocumented sentinel of that source's
// own, distinct from the official one - and its own z_min/z_max summary
// attributes report the very same value, so the exporter itself isn't
// distinguishing "no data" from "an extreme value" either. Treating any
// implausibly deep Z (below -50m - no LER utility in Denmark is buried
// anywhere near that, and Danish terrain itself rarely dips below -10m)
// as unspecified catches -99, -989 and whatever similar sentinel another
// source might use, without hardcoding an exhaustive list of them -
// the alternative was single vertices spiking ~1000m below the rest of
// their own line, corrupting both the geometry and any depth/volume math
// downstream that reads a "measured" Z at face value.
function isUnspecifiedZ(z){
	return z === 0 || z <= -50;
}

// The official schema states its unit explicitly via a uom attribute
// (almost always "mm", occasionally "m") - convert accordingly rather than
// assuming.
function toMeters(value, uom){
	if(uom === "mm") return value / 1000;
	if(uom === "cm") return value / 100;
	return value; // "m" or unspecified -> already meters
}

// The flattened WFS schema doesn't carry a uom attribute on these fields.
// Real depths/diameters here are on the order of 0.1-3m or 30-3000mm, so a
// value over ~20 is unambiguously millimeters.
export function toMetersGuessingUnit(value){
	return value > 20 ? value / 1000 : value;
}

function supplyFromTableName(tableName){
	if(!tableName){
		return "andet";
	}
	let lower = tableName.toLowerCase();
	for(let [prefix, supply] of SUPPLY_PREFIXES){
		if(lower.startsWith(prefix)){
			return supply;
		}
	}
	return "andet";
}

function findText(elem, ...names){
	for(let name of names){
		let child = null;

		if(name.startsWith("ler:")){
			let matches = elem.getElementsByTagNameNS(LER_NS, name.slice(4));
			child = matches.length > 0 ? matches[0] : null;
		}else{
			let matches = elem.getElementsByTagName(name);
			child = matches.length > 0 ? matches[0] : null;
			if(!child){
				let nsMatches = elem.getElementsByTagNameNS(LER_NS, name);
				child = nsMatches.length > 0 ? nsMatches[0] : null;
			}
		}

		if(child && child.textContent && child.textContent.trim()){
			return child.textContent.trim();
		}
	}

	return null;
}

// Any unprefixed direct/indirect child, matched purely by local name -
// used for the flattened WFS schema, whose attributes live in a
// deployment-specific namespace (the municipality's own, e.g.
// "http://dannevang_kommune") rather than a fixed one like ler:.
function findAnyText(elem, name){
	for(let el of elem.getElementsByTagName("*")){
		if(el.localName === name && el.textContent && el.textContent.trim()){
			return el.textContent.trim();
		}
	}
	return null;
}

// Best-effort attribute lookup for a customer WFS whose schema is entirely
// unknown ahead of time: scans every leaf element (no children - actual
// values, not geometry/property wrappers) for one whose local name
// contains any of `patterns`, case-insensitively. Returns the first hit's
// {name, value, uom}, or null. This is inherently heuristic - see
// extractGenericFeature() for how each result is used.
function findAttributeMatching(elem, patterns){
	for(let el of elem.getElementsByTagName("*")){
		if(el.children.length > 0 || !el.textContent || !el.textContent.trim()){
			continue;
		}
		let name = el.localName.toLowerCase();
		if(patterns.some(p => name.includes(p))){
			return {name: el.localName, value: el.textContent.trim(), uom: el.getAttribute("uom")};
		}
	}
	return null;
}

export const GENERIC_ATTRIBUTE_PATTERNS = {
	diameter: ["diameter", "dimension"],
	dybde: ["vejledende_dybde", "dybde", "depth"],
	// Danish utilities commonly give sewer/drain lines by their internal
	// invert ("indvendig bundkote") rather than a top-referenced Z or depth
	// - the one place LE34 has seen this differ from the LER WFS schema.
	bundkote: ["indv_bundkote", "indvendig_bundkote", "bundkote", "invert"],
	owner: ["ledningsejer_navn", "ejer", "selskab", "company", "ownername"],
	id: ["u_id", "objectid", "gid", "uuid", "id"],
	status: ["driftsstatus", "status"],
	danger: ["fareklasse", "danger"],
	type: ["type", "kategori", "category"],
};

// A layer name like "AarhusVandFiles:VAND_LEDNING_MV" or "KM_L_LEDNING_MV"
// tells you almost nothing generically, but Danish utility naming
// conventions are consistent enough for a reasonable first guess - the UI
// still lets the user override it per layer.
export function guessSupplyFromLayerName(name){
	let lower = name.toLowerCase();
	if(lower.includes("spild") || lower.includes("kloak") || lower.includes("afloeb") || lower.includes("afløb")) return "afloeb";
	if(lower.includes("varme") || lower.includes("termisk")) return "termisk";
	if(lower.includes("tele") || lower.includes("fiber")) return "telekom";
	if(lower.includes("roer") || lower.includes("føring") || lower.includes("foering")) return "foeringsroer";
	if(lower.includes("_el_") || lower.includes("elled") || lower.includes("elkab") || /(^|[_-])el([_-]|$)/.test(lower)) return "el";
	if(lower.includes("vand")) return "vand";
	return "andet";
}

// Schema-agnostic extraction for a customer WFS: geometry kind is read
// straight from the GML (Point vs LineString/MultiCurve) rather than
// assumed from a type name, and every other value is best-effort attribute
// sniffing (see findAttributeMatching). `supply` is assigned by the user
// per layer (see the "Kunde WFS" UI) since there's no reliable way to
// infer it from an arbitrary customer's own naming.
// Bladene i et WFS-objekt (navn -> tekst) uden geometrien, til at farve et lag efter en attribut (se LayerStyle.js)
function leafAttributes(elem){
	let attrs = {};
	let count = 0;
	for(let el of elem.getElementsByTagName("*")){
		if(el.children.length > 0 || el.namespaceURI === GML_NS) continue;
		let text = el.textContent ? el.textContent.trim() : "";
		if(text === "" || attrs[el.localName] !== undefined) continue;
		attrs[el.localName] = text;
		if(++count >= 80) break;
	}
	return attrs;
}

function extractGenericFeature(elem, groundAt, fallbackDepthM, supply, layerLabel){
	let isLine = elem.getElementsByTagNameNS(GML_NS, "LineString").length > 0
		|| elem.getElementsByTagNameNS(GML_NS, "MultiCurve").length > 0;
	let isPoint = !isLine && elem.getElementsByTagNameNS(GML_NS, "Point").length > 0;
	if(!isLine && !isPoint){
		return null;
	}

	let coords = isLine ? extractLineCoords(elem) : extractPointCoords(elem);
	if(!coords){
		return null;
	}

	let diameterAttr = findAttributeMatching(elem, GENERIC_ATTRIBUTE_PATTERNS.diameter);
	let diameterM = diameterAttr ? toMetersGuessingUnit(parseFloat(diameterAttr.value)) : null;
	if(diameterM !== null && Number.isNaN(diameterM)) diameterM = null;

	let dybdeAttr = findAttributeMatching(elem, GENERIC_ATTRIBUTE_PATTERNS.dybde);
	let dybdeM = dybdeAttr ? toMetersGuessingUnit(parseFloat(dybdeAttr.value)) : null;
	if(dybdeM !== null && Number.isNaN(dybdeM)) dybdeM = null;

	let ownerAttr = findAttributeMatching(elem, GENERIC_ATTRIBUTE_PATTERNS.owner);
	let idAttr = findAttributeMatching(elem, GENERIC_ATTRIBUTE_PATTERNS.id);
	let statusAttr = findAttributeMatching(elem, GENERIC_ATTRIBUTE_PATTERNS.status);
	let dangerAttr = findAttributeMatching(elem, GENERIC_ATTRIBUTE_PATTERNS.danger);
	let typeAttr = findAttributeMatching(elem, GENERIC_ATTRIBUTE_PATTERNS.type);

	let resolved, sources;
	let bundkoteAttr = supply === "afloeb" ? findAttributeMatching(elem, GENERIC_ATTRIBUTE_PATTERNS.bundkote) : null;
	let bundkoteM = bundkoteAttr ? parseFloat(bundkoteAttr.value) : null;

	if(bundkoteM !== null && !Number.isNaN(bundkoteM) && !isUnspecifiedZ(bundkoteM)){
		// Internal invert -> outside top-of-pipe: add the pipe's own
		// diameter (wall thickness is a minor, accepted approximation).
		let topZ = bundkoteM + (diameterM || DEFAULT_DIAMETER_M);
		resolved = coords.map(([x, y]) => [x, y, topZ]);
		sources = coords.map(() => "bundkote");
	}else{
		({resolved, sources} = resolveZ(coords, dybdeM, defaultDepthFor(supply, fallbackDepthM), groundAt, false));
	}

	let ownerLabel = ownerAttr ? ownerAttr.value : layerLabel;

	return {
		owner: ownerLabel, ownerName: ownerLabel, id: idAttr ? idAttr.value : null,
		type: typeAttr ? typeAttr.value : layerLabel, supply,
		geometry: isLine ? "line" : "point", is_trace: false,
		diameter_m: diameterM || DEFAULT_DIAMETER_M,
		subtype: typeAttr ? typeAttr.value : null,
		status: statusAttr ? statusAttr.value : null,
		danger: dangerAttr ? dangerAttr.value : null,
		attrs: leafAttributes(elem),
		z_sources: sources, coords: resolved,
	};
}

function parsePosList(text){
	let values = text.trim().split(/\s+/).map(Number);
	let coords = [];
	for(let i = 0; i < values.length; i += 3){
		coords.push([values[i], values[i + 1], values[i + 2]]);
	}
	return coords;
}

// Searches the whole feature element for GML geometry, regardless of what
// the wrapping property is called (ler:geometri, <ns>:geom, ...) - works
// for both schemas.
function extractLineCoords(elem){
	let coords = [];
	for(let posList of elem.getElementsByTagNameNS(GML_NS, "posList")){
		coords.push(...parsePosList(posList.textContent));
	}
	return coords.length > 0 ? coords : null;
}

function extractPointCoords(elem){
	let posElements = elem.getElementsByTagNameNS(GML_NS, "pos");
	if(posElements.length === 0){
		return null;
	}
	let values = posElements[0].textContent.trim().split(/\s+/).map(Number);
	return [values];
}

// Extra, informational attributes shown in the feature tooltip (see gis.vue's
// formatFeatureInfo): accuracy classes, establishment time, Z range and the
// guide depth *as given by the owner*. Read separately from the geometry/
// depth resolution above so a schema that lacks any of them just leaves the
// field null (the UI only shows what's actually specified).
//
//  - "official": the LER GML schema's own ler:-namespaced elements.
//  - "flat" / "generic": whatever leaf element's local name matches - a
//    municipality's flattened WFS or a customer WFS uses its own naming
//    (snake_case, other prefixes), so this is a name-based best effort.
function firstLeafText(elem, predicate){
	for(let el of elem.getElementsByTagName("*")){
		if(el.children.length > 0 || !el.textContent || !el.textContent.trim()){
			continue;
		}
		if(predicate(el.localName.toLowerCase())){
			return el.textContent.trim();
		}
	}
	return null;
}

const stripNonLetters = (name) => name.replace(/[^a-z]/g, "");

function parseSpecifiedNumber(text){
	if(text === null || text === undefined){
		return null;
	}
	let value = parseFloat(String(text).replace(",", "."));
	return (Number.isFinite(value) && !isUnspecifiedZ(value)) ? value : null;
}

function addExtraAttributes(feature, elem, mode){
	let accuracy, accuracyVertical, established, zMaxText, zMinText, guideDepth = null;

	if(mode === "official"){
		accuracy = findText(elem, "ler:noejagtighedsklasse");
		accuracyVertical = findText(elem, "ler:noejagtighedsklasseVertikal");
		established = findText(elem, "ler:etableringstidspunkt");
		let dybdeElem = elem.getElementsByTagNameNS(LER_NS, "vejledendeDybde")[0];
		if(dybdeElem && dybdeElem.textContent && dybdeElem.textContent.trim()){
			let value = toMeters(parseFloat(dybdeElem.textContent), dybdeElem.getAttribute("uom"));
			guideDepth = Number.isFinite(value) ? value : null;
		}
	}else{
		let isVertical = (n) => n.includes("noejagtighed") && n.includes("vertikal") || n.includes("nojagtighed") && n.includes("vertikal");
		let isAccuracy = (n) => (n.includes("noejagtighed") || n.includes("nojagtighed")) && !n.includes("vertikal") && !n.includes("vertical");
		accuracy = firstLeafText(elem, isAccuracy);
		accuracyVertical = firstLeafText(elem, isVertical);
		established = firstLeafText(elem, (n) => (n.includes("etableringstidspunkt") || n.includes("etableringsdato") || n === "etableret"));
		let depthText = firstLeafText(elem, (n) => n === "vejledende_dybde" || n === "vejledendedybde");
		if(depthText !== null){
			let value = toMetersGuessingUnit(parseFloat(depthText.replace(",", ".")));
			guideDepth = Number.isFinite(value) ? value : null;
		}
	}

	zMaxText = firstLeafText(elem, (n) => stripNonLetters(n) === "zmax");
	zMinText = firstLeafText(elem, (n) => stripNonLetters(n) === "zmin");

	// An explicit Z_max/Z_min attribute wins; otherwise fall back to the
	// range of the feature's own *measured* coordinate Z (never one derived
	// from ground level minus a depth - that isn't "angivet").
	let zMax = parseSpecifiedNumber(zMaxText);
	let zMin = parseSpecifiedNumber(zMinText);
	if((zMax === null || zMin === null) && feature.coords && feature.z_sources){
		let measured = feature.coords.filter((c, i) => feature.z_sources[i] === "measured").map(c => c[2]);
		if(measured.length > 0){
			if(zMax === null) zMax = Math.max(...measured);
			if(zMin === null) zMin = Math.min(...measured);
		}
	}

	feature.accuracy = accuracy || null;
	feature.accuracy_vertical = accuracyVertical || null;
	feature.established = established || null;
	feature.z_max = zMax;
	feature.z_min = zMin;
	feature.guide_depth_m = guideDepth;
	return feature;
}

function resolveZ(coords, vejledendeDybdeM, defaultDepthM, groundAt, sitsOnGround){
	let resolved = [];
	let sources = [];

	for(let [x, y, z] of coords){
		if(!isUnspecifiedZ(z)){
			resolved.push([x, y, z]);
			sources.push("measured");
			continue;
		}

		let groundZ = groundAt(x, y);

		if(sitsOnGround){
			resolved.push([x, y, groundZ]);
			sources.push("ground_level");
		}else if(vejledendeDybdeM !== null){
			resolved.push([x, y, groundZ - vejledendeDybdeM]);
			sources.push("vejledende_dybde");
		}else{
			resolved.push([x, y, groundZ - defaultDepthM]);
			sources.push("default_depth");
		}
	}

	return {resolved, sources};
}

function parseOwners(doc){
	let owners = {};
	for(let elem of doc.getElementsByTagNameNS(LER_NS, "UtilityOwner")){
		let ownerId = findText(elem, "ledningsejer");
		let company = findText(elem, "companyName");
		if(ownerId && company){
			owners[ownerId] = company;
		}
	}
	return owners;
}

// All feature elements in `doc`, regardless of which member-wrapper style
// it uses: the classic <gml:featureMember> (the official LER GML export)
// or WFS 2.0's <wfs:member> (a live GetFeature response).
function getFeatureElements(doc){
	let elements = [];
	for(let member of doc.getElementsByTagNameNS(GML_NS, "featureMember")){
		if(member.children[0]) elements.push(member.children[0]);
	}
	for(let member of doc.getElementsByTagNameNS(WFS_NS, "member")){
		if(member.children[0]) elements.push(member.children[0]);
	}
	return elements;
}

function extractOfficialFeature(elem, tag, groundAt, fallbackDepthM){
	let ownerId = findText(elem, "ledningsejer");
	let featureId = findText(elem, "ler:id", "id") || elem.getAttributeNS(GML_NS, "id");

	let diameterElem = elem.getElementsByTagNameNS(LER_NS, "udvendigDiameter")[0];
	let diameterM = diameterElem && diameterElem.textContent
		? toMeters(parseFloat(diameterElem.textContent), diameterElem.getAttribute("uom"))
		: null;

	let dybdeElem = elem.getElementsByTagNameNS(LER_NS, "vejledendeDybde")[0];
	let dybdeM = dybdeElem && dybdeElem.textContent
		? toMeters(parseFloat(dybdeElem.textContent), dybdeElem.getAttribute("uom"))
		: null;

	let subtype = findText(elem, "ler:type");
	let status = findText(elem, "ler:driftsstatus");
	let danger = findText(elem, "ler:fareklasse");
	let niveau = findText(elem, "ler:niveau");

	if(LINE_TYPES[tag]){
		let supply = LINE_TYPES[tag];
		let coords = extractLineCoords(elem);
		if(!coords) return null;
		let {resolved, sources} = resolveZ(coords, dybdeM, defaultDepthFor(supply, fallbackDepthM), groundAt, false);
		return {
			owner: ownerId, ownerName: null, id: featureId, type: tag, supply,
			geometry: "line", is_trace: false, diameter_m: diameterM || DEFAULT_DIAMETER_M,
			subtype, status, danger, z_sources: sources, coords: resolved,
		};
	}

	if(POINT_TYPES[tag]){
		let supply = POINT_TYPES[tag];
		let coords = extractPointCoords(elem);
		if(!coords) return null;
		let sitsOnGround = niveau === "over terræn";
		let {resolved, sources} = resolveZ(coords, dybdeM, defaultDepthFor(supply, fallbackDepthM), groundAt, sitsOnGround);
		return {
			owner: ownerId, ownerName: null, id: featureId, type: tag, supply,
			geometry: "point", is_trace: false, diameter_m: diameterM,
			subtype, status, danger, z_sources: sources, coords: resolved,
		};
	}

	if(tag === TRACE_TYPE){
		let coords = extractLineCoords(elem);
		if(!coords) return null;
		let forsyningsart = findText(elem, "ler:forsyningsart");
		let supply = TRACE_SUPPLY[forsyningsart] || "andet";
		let {resolved, sources} = resolveZ(coords, dybdeM, defaultDepthFor(supply, fallbackDepthM), groundAt, false);
		return {
			owner: ownerId, ownerName: null, id: featureId, type: tag, supply,
			geometry: "line", is_trace: true, diameter_m: diameterM || DEFAULT_DIAMETER_M,
			subtype: forsyningsart, status, danger, z_sources: sources, coords: resolved,
		};
	}

	return undefined; // not a recognized official-schema feature type
}

function extractFlatWFSFeature(elem, tag, groundAt, fallbackDepthM){
	let tableName = findAnyText(elem, "table_name");
	if(!tableName){
		return undefined; // not a recognized flattened-WFS feature
	}

	let isPoint = tag.includes("komponent");
	let isLine = tag.includes("ledning");
	if(!isPoint && !isLine){
		return undefined;
	}

	let ownerId = findAnyText(elem, "ledningsejer_cvr");
	let ownerName = findAnyText(elem, "ledningsejer_navn");
	let featureId = findAnyText(elem, "u_id") || findAnyText(elem, "id");
	let supply = supplyFromTableName(tableName);

	let diameterText = findAnyText(elem, "udvendig_diameter");
	let diameterM = diameterText ? toMetersGuessingUnit(parseFloat(diameterText)) : null;

	let dybdeText = findAnyText(elem, "vejledende_dybde");
	let dybdeM = dybdeText ? toMetersGuessingUnit(parseFloat(dybdeText)) : null;

	let subtype = findAnyText(elem, "type") || tableName;
	let status = findAnyText(elem, "driftsstatus");
	let danger = findAnyText(elem, "fareklasse");
	let niveau = findAnyText(elem, "niveau");

	let depthM = defaultDepthFor(supply, fallbackDepthM);

	if(isLine){
		let coords = extractLineCoords(elem);
		if(!coords) return null;
		let {resolved, sources} = resolveZ(coords, dybdeM, depthM, groundAt, false);
		return {
			owner: ownerId, ownerName, id: featureId, type: tableName, supply,
			geometry: "line", is_trace: false, diameter_m: diameterM || DEFAULT_DIAMETER_M,
			subtype, status, danger, z_sources: sources, coords: resolved,
		};
	}

	let coords = extractPointCoords(elem);
	if(!coords) return null;
	let sitsOnGround = niveau === "over terræn";
	let {resolved, sources} = resolveZ(coords, dybdeM, depthM, groundAt, sitsOnGround);
	return {
		owner: ownerId, ownerName, id: featureId, type: tableName, supply,
		geometry: "point", is_trace: false, diameter_m: diameterM,
		subtype, status, danger, z_sources: sources, coords: resolved,
	};
}

// Extracts owners + features (still in absolute, non-offset coordinates)
// from one parsed XML document (official LER schema or the flattened WFS
// schema, auto-detected per feature). Used by both parseLERDocument (a
// single GML file) and parseLERDocuments (several WFS responses merged
// together); also exported directly as extractLERPart() for accumulating
// onto an existing LERLayer via addFeatures().
export async function extractLERPart(doc, groundAt, defaultDepthM = 1.0){
	return extractFeatures(doc, groundAt, defaultDepthM);
}

// Every YIELD_EVERY_N_FEATURES iterations, this hands control back to the
// browser for a tick (a bare setTimeout(0)) instead of running the whole
// loop in one synchronous burst. Found necessary the hard way: a WFS
// response for a real, densely-utility-mapped area can carry many
// thousands of features, each needing several getElementsByTagName* walks
// of its own (extractOfficialFeature/extractFlatWFSFeature/
// addExtraAttributes) - fully synchronous, that easily runs long enough
// for Chrome's own hung-renderer watchdog to kill the tab outright
// (RESULT_CODE_HUNG), which a client-side network timeout around the
// fetch() call itself does nothing for - by the time this loop runs, the
// response has already arrived; the hang is pure main-thread JS after
// that. Yielding periodically keeps the tab painting/responsive (and a
// future "cancel" button viable) regardless of how large the response is,
// at the cost of a handful of extra event-loop turns - negligible next to
// a multi-second parse.
const YIELD_EVERY_N_FEATURES = 200;
const yieldToBrowser = () => new Promise(resolve => setTimeout(resolve, 0));

async function extractFeatures(doc, groundAt, defaultDepthM){
	let owners = parseOwners(doc);
	let features = [];

	let i = 0;
	for(let elem of getFeatureElements(doc)){
		let tag = elem.localName;

		let feature = extractOfficialFeature(elem, tag, groundAt, defaultDepthM);
		let mode = "official";
		if(feature === undefined){
			feature = extractFlatWFSFeature(elem, tag, groundAt, defaultDepthM);
			mode = "flat";
		}
		if(feature){
			addExtraAttributes(feature, elem, mode);

			if(feature.ownerName && feature.owner && !owners[feature.owner]){
				owners[feature.owner] = feature.ownerName;
			}
			delete feature.ownerName;

			features.push(feature);
		}

		i++;
		if(i % YIELD_EVERY_N_FEATURES === 0){
			await yieldToBrowser();
		}
	}

	return {owners, features};
}

function finalize(parts){
	let owners = {};
	let features = [];
	for(let part of parts){
		Object.assign(owners, part.owners);
		features.push(...part.features);
	}

	if(features.length === 0){
		throw new Error("no line/point features with usable geometry found");
	}

	let offset = [Infinity, Infinity, Infinity];
	for(let feature of features){
		for(let [x, y, z] of feature.coords){
			offset[0] = Math.min(offset[0], x);
			offset[1] = Math.min(offset[1], y);
			offset[2] = Math.min(offset[2], z);
		}
	}

	for(let feature of features){
		feature.coords = feature.coords.map(([x, y, z]) => [x - offset[0], y - offset[1], z - offset[2]]);
	}

	return {offset, owners, features};
}

// Extracts a {owners, features} part (absolute coords) from a customer
// WFS layer's response, given the supply type and display label the user
// assigned to that layer in the "Kunde WFS" UI (see index.html).
async function extractGenericLayerFeatures(doc, groundAt, defaultDepthM, supply, layerLabel){
	let owners = {};
	let features = [];

	let i = 0;
	for(let elem of getFeatureElements(doc)){
		let feature = extractGenericFeature(elem, groundAt, defaultDepthM, supply, layerLabel);
		if(feature){
			addExtraAttributes(feature, elem, "generic");

			if(feature.ownerName && !owners[feature.owner]){
				owners[feature.owner] = feature.ownerName;
			}
			delete feature.ownerName;

			features.push(feature);
		}

		i++;
		if(i % YIELD_EVERY_N_FEATURES === 0){
			await yieldToBrowser();
		}
	}

	return {owners, features};
}

// entries: [{doc, supply, label}] - one per selected customer-WFS layer.
// Each layer becomes its own "owner" bucket (its label) in the resulting
// part unless a real per-feature owner attribute was found, which doubles
// as the "turn layers on/off" UI the existing owner checkboxes already
// provide - no separate layer-visibility mechanism needed.
export async function extractGenericWFSPart(entries, groundAt, defaultDepthM = 1.0){
	let owners = {};
	let features = [];

	for(let {doc, supply, label} of entries){
		let part = await extractGenericLayerFeatures(doc, groundAt, defaultDepthM, supply, label);
		Object.assign(owners, part.owners);
		features.push(...part.features);
	}

	return {owners, features};
}

// Merges one or more {owners, features} parts (absolute coords, as
// returned by extractGenericWFSPart or produced internally by
// parseLERDocument(s)) into a single result with one shared offset.
// Exposed so index.html can accumulate multiple sources - a GML file, the
// official LER WFS, one or more customer WFS layers - into the same
// LERLayer over time; see LERLayer.addFeatures() in LER.js for adding a
// part to an *existing* layer instead (reusing its already-fixed offset).
export function finalizeLERParts(parts){
	return finalize(parts);
}

// groundAt(x, y) -> elevation in meters, absolute (same frame as the
// document's own coordinates - i.e. NOT yet offset). See buildGroundSampler().
export async function parseLERDocument(doc, groundAt, defaultDepthM = 1.0){
	return finalize([await extractFeatures(doc, groundAt, defaultDepthM)]);
}

// Same as parseLERDocument, but merges several documents (e.g. separate
// WFS responses for lines and for components) into one consistent result
// with a single shared offset - see LERWFS.js.
export async function parseLERDocuments(docs, groundAt, defaultDepthM = 1.0){
	return finalize(await Promise.all(docs.map(doc => extractFeatures(doc, groundAt, defaultDepthM))));
}

// Walks the octree's actually-loaded nodes, wherever they are, rather than
// only pointcloud.visibleNodes (which reflects the *current camera view* -
// pan or zoom away and it shrinks to whatever's on screen). pointcloud.root
// starts out loaded once anything renders at all, and even at just that
// coarse top level it already carries a sparse sampling spread across the
// *entire* cloud extent, not merely whatever's currently framed. Each node
// is either a rendering wrapper (has .geometryNode, the actual data
// object) or, if not yet streamed in, a bare geometry-node placeholder
// (no .geometry yet) - handled uniformly here.
export function collectLoadedGeometryNodes(root){
	let result = [];
	let stack = root ? [root] : [];

	while(stack.length > 0){
		let node = stack.pop();
		let geometryNode = node.geometryNode || node;

		if(geometryNode.geometry){
			result.push(geometryNode);
		}

		// node.children is an array on rendering-wrapper nodes but a plain
		// {0: ..., 3: ..., ...} object on raw (not-yet-rendered) geometry
		// nodes - Object.values() handles both uniformly.
		for(let child of Object.values(node.children || {})){
			if(child){
				stack.push(child);
			}
		}
	}

	return result;
}

// Builds a ground-elevation sampling function from `pointcloud`'s
// already-loaded octree data - see collectLoadedGeometryNodes() above for
// why that's the whole loaded tree and not just the current camera view.
// Points whose (x, y) fall outside that coverage (e.g. LER features in a
// WFS buffer margin beyond the point cloud's own footprint) fall back to
// the nearest populated cell rather than being left unresolved.
//
// Mirrors the CPU-side octree read used by Potree's own profile tool
// (ProfileRequest.js): point positions are stored relative to each node's
// own bounding box, so pointcloud.matrixWorld alone isn't enough - each
// node's boundingBox.min has to be added back in first.
export function buildGroundSampler(pointcloud, THREE, cellSize = 1.0){
	if(!pointcloud){
		return null;
	}

	let cells = new Map(); // "ix,iy" -> [z, z, ...]
	let key = (ix, iy) => `${ix},${iy}`;

	for(let geometryNode of collectLoadedGeometryNodes(pointcloud.root)){
		let geometry = geometryNode.geometry;
		if(!geometry.attributes.classification){
			continue;
		}

		let numPoints = geometryNode.numPoints;
		let position = geometry.attributes.position.array;
		let classification = geometry.attributes.classification.array;

		let nodeMatrix = new THREE.Matrix4().makeTranslation(...geometryNode.boundingBox.min.toArray());
		let matrix = new THREE.Matrix4().multiplyMatrices(pointcloud.matrixWorld, nodeMatrix);

		let v = new THREE.Vector3();
		for(let i = 0; i < numPoints; i++){
			if(classification[i] !== 2){
				continue;
			}

			v.set(position[i * 3], position[i * 3 + 1], position[i * 3 + 2]);
			v.applyMatrix4(matrix);

			let ix = Math.floor(v.x / cellSize);
			let iy = Math.floor(v.y / cellSize);
			let k = key(ix, iy);

			if(!cells.has(k)){
				cells.set(k, []);
			}
			cells.get(k).push(v.z);
		}
	}

	if(cells.size === 0){
		return null;
	}

	let medians = new Map();
	for(let [k, values] of cells){
		values.sort((a, b) => a - b);
		medians.set(k, values[Math.floor(values.length / 2)]);
	}

	let allKeys = [...medians.keys()].map(k => k.split(",").map(Number));

	return function(x, y){
		let ix = Math.floor(x / cellSize);
		let iy = Math.floor(y / cellSize);
		let k = key(ix, iy);

		if(medians.has(k)){
			return medians.get(k);
		}

		// nearest populated cell - handles points outside sampled coverage
		// entirely (e.g. a WFS buffer margin beyond the point cloud itself)
		let best = null;
		let bestDist = Infinity;
		for(let [kx, ky] of allKeys){
			let dist = (kx - ix) ** 2 + (ky - iy) ** 2;
			if(dist < bestDist){
				bestDist = dist;
				best = key(kx, ky);
			}
		}

		return medians.get(best);
	};
}
